import { useEffect, useRef, useState } from "react";
import { Download, X } from "lucide-react";
import { downloadName, plural } from "./meet-gary-catalog";
import { buildRegionZip, regionZipName, zipEntries } from "./region-zip";

const PANEL_TITLE = "Business Owner HSA - HSA Tax Savings Stories";
const PANEL_INTRO = "Same tax comparison, different client story. Pick the business owner who looks most like the person you’re meeting.";

const STEP_HEADING = "mb-[6px] text-[13px] font-bold text-gray-900";

const softChoice = (on) =>
    on ? "border-[#1e85e5] bg-[#eaf3fd] text-[#115aa1]" : "border-gray-200 bg-white text-gray-700 hover:border-[#1e85e5]/40";

const solidChoice = (on) =>
    on ? "border-[#082e54] bg-[#082e54] text-[#fff]" : "border-gray-200 bg-white text-gray-700 hover:border-gray-400";

const storyChoice = (on, available) => {
    if (!available) return "cursor-not-allowed border-gray-200 bg-white opacity-[0.55]";
    return on ? "border-[#1e85e5] bg-[#eaf3fd]" : "border-gray-200 bg-white hover:border-[#1e85e5]/40";
};

const saveBytes = (bytes, filename) => {
    const url = URL.createObjectURL(new Blob([bytes], { type: "application/zip" }));
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 30000);
};

const MeetGaryPanel = ({ catalog, onClose }) => {
    const [region, setRegion] = useState(catalog.defaultRegion);
    const [storyId, setStoryId] = useState(() => catalog.resolveStory(undefined, catalog.defaultRegion));
    const [industry, setIndustry] = useState("all");
    const [zipState, setZipState] = useState("idle");
    const closeRef = useRef(null);

    useEffect(() => {
        closeRef.current?.focus();
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [onClose]);

    const industries = catalog.industriesFor(region);
    const showIndustry = industries.length >= 2;
    const listedIndustry = showIndustry ? industry : "all";
    const roster = catalog.rosterFor(region, listedIndustry);
    const story = catalog.storiesFor(region, listedIndustry).find((s) => s.id === storyId);
    const file = story && catalog.fileFor(story.id, region);
    const fileName = story && downloadName(story, region);

    const pickRegion = (next) => {
        const nextIndustry = catalog.resolveIndustry(industry, next);
        setRegion(next);
        setIndustry(nextIndustry);
        setStoryId(catalog.resolveStory(storyId, next, nextIndustry));
        setZipState("idle");
    };

    const pickIndustry = (next) => {
        setIndustry(next);
        setStoryId(catalog.resolveStory(storyId, region, next));
    };

    const downloadZip = async () => {
        setZipState("preparing");
        try {
            const bytes = await buildRegionZip(zipEntries(catalog, region));
            saveBytes(bytes, regionZipName(region));
            setZipState("idle");
        } catch {
            setZipState("error");
        }
    };

    return (
        <section
            role="dialog"
            aria-labelledby="meet-gary-title"
            className="fixed inset-0 z-[100] flex flex-col overflow-hidden bg-white xl:inset-auto xl:top-20 xl:right-6 xl:bottom-6 xl:w-[400px] xl:rounded-2xl xl:border xl:border-gray-200 xl:shadow-modal"
        >
            <div className="flex-none pl-5 pr-[64px] pt-[20px] pb-3">
                <div className="min-w-0">
                    <p className="text-[11px] font-bold tracking-[0.12em] text-primary-darker">
                        {`DOCUMENT FAMILY · ${plural(catalog.storyCount, "STORY", "STORIES")}`}
                    </p>
                    <h2 id="meet-gary-title" className="mt-1 font-display text-xl leading-snug text-gray-900 break-words">
                        {PANEL_TITLE}
                    </h2>
                </div>
                <button
                    ref={closeRef}
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute right-3 top-3 flex h-[44px] w-[44px] items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                >
                    <X className="h-5 w-5" aria-hidden="true" />
                </button>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-y-[16px] overflow-y-auto overscroll-contain pl-5 pr-5 pt-[4px] pb-[20px]">
                <p className="text-sm leading-relaxed text-gray-700">{PANEL_INTRO}</p>

                <div>
                    <p className={STEP_HEADING}>1. Region</p>
                    <div role="group" aria-label="Region" className="grid grid-cols-3 gap-[6px]">
                        {catalog.regions.map((r) => (
                            <button
                                key={r}
                                type="button"
                                aria-pressed={r === region ? "true" : "false"}
                                onClick={() => pickRegion(r)}
                                className={`min-h-[44px] rounded-lg border-[1px] text-sm font-semibold transition-colors xl:min-h-[40px] ${softChoice(r === region)}`}
                            >
                                {r}
                            </button>
                        ))}
                    </div>
                    <p className="mt-[6px] text-[12px] text-gray-500">
                        Each region uses its own provincial tax treatment in the comparison table.
                    </p>
                </div>

                <div>
                    {showIndustry ? (
                        <>
                            <p className={STEP_HEADING}>2. Client’s industry</p>
                            <div role="group" aria-label="Client’s industry" className="mb-[10px] flex flex-wrap gap-[6px]">
                                {[{ id: "all", label: "All" }, ...industries].map((g) => (
                                    <button
                                        key={g.id}
                                        type="button"
                                        aria-pressed={g.id === industry ? "true" : "false"}
                                        onClick={() => pickIndustry(g.id)}
                                        className={`min-h-[44px] rounded-full border-[1px] pl-2.5 pr-2.5 pt-1 pb-1 text-[13px] transition-colors xl:min-h-[32px] ${solidChoice(g.id === industry)}`}
                                    >
                                        {g.label}
                                    </button>
                                ))}
                            </div>
                        </>
                    ) : (
                        <p className={STEP_HEADING}>2. Business owner story</p>
                    )}
                    <div className="grid grid-cols-2 gap-[6px]">
                        {roster.map((s) => (
                            <button
                                key={s.id}
                                type="button"
                                disabled={!s.available}
                                aria-pressed={s.id === storyId && s.available ? "true" : "false"}
                                onClick={() => setStoryId(s.id)}
                                className={`flex min-h-[52px] min-w-0 items-center gap-2 rounded-[10px] border-[1px] p-2 text-left transition-colors ${storyChoice(s.id === storyId, s.available)}`}
                            >
                                <span
                                    aria-hidden="true"
                                    className="flex h-[32px] w-[32px] flex-none items-center justify-center rounded-full bg-[#c7e0fa] text-[13px] font-bold text-[#082e54]"
                                >
                                    {s.name.charAt(0)}
                                </span>
                                <span className="flex min-w-0 flex-col">
                                    <span className="text-sm font-bold text-[#0a0e14]">{s.name}</span>
                                    <span className="break-words text-[12px] text-gray-600">{s.industry}</span>
                                    {!s.available && (
                                        <span className="text-[11px] text-gray-500">{`${s.regions.join(", ")} only`}</span>
                                    )}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                <div>
                    <p className={STEP_HEADING}>3. Preview</p>
                    {file && (
                        <figure className="flex flex-col items-center gap-2 rounded-xl bg-gray-50 pt-2 pb-2 pl-3 pr-3">
                            <img
                                src={file.thumbnail}
                                alt={`Preview of ${fileName}`}
                                className="max-h-[220px] w-auto rounded border border-gray-200"
                            />
                            <figcaption className="break-words text-center text-xs text-gray-600">{fileName}</figcaption>
                        </figure>
                    )}
                </div>
            </div>

            <div className="flex flex-none flex-col gap-2 border-t border-gray-100 bg-white pl-5 pr-5 pt-[16px] pb-[20px]">
                {file && (
                    <a
                        href={file.href}
                        download={fileName}
                        className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-primary font-semibold text-[#fff] no-underline shadow-sm hover:bg-primary-dark hover:text-[#fff]"
                    >
                        <Download className="h-5 w-5" aria-hidden="true" />
                        Download
                    </a>
                )}
                <button
                    type="button"
                    onClick={downloadZip}
                    disabled={zipState === "preparing"}
                    className="min-h-[44px] rounded-lg text-sm font-semibold text-primary-darker hover:underline disabled:cursor-wait disabled:opacity-60"
                >
                    {zipState === "preparing" ? "Preparing ZIP…" : `Download all ${region} stories (ZIP)`}
                </button>
                {zipState === "error" && (
                    <p role="alert" className="text-sm text-red-700">
                        Couldn’t build the ZIP. Download the stories one at a time.
                    </p>
                )}
            </div>
        </section>
    );
};

export default MeetGaryPanel;
